import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';

import { useAuth } from '../../contexts/AuthContext';
import { useResiAccess } from 'hooks/resi/useResiAccess';
import { resiRouteMatchers } from 'permissions/resi-routes';
import { resiRouteAccessRules } from 'permissions/resi-route-access';
import { getRouteAccessDecision } from 'permissions/getRouteAccessDecision';
import { PropertyAccessLevel } from 'utils/resi/access/propertyAccessLevel';
import { ResiPermissions } from 'permissions/resi-permissions';

export const RESI_ROUTER_NAMESPACE = '/consumer';
// TODO: We should get rid of this because we want to have a dashboard available to EVERYONE that has a sage account
const RESI_ROUTE_FALLBACKS = [
    {
        path: `${RESI_ROUTER_NAMESPACE}/guests`,
        permission: ResiPermissions.VIEW_GUESTS
    },
    {
        path: `${RESI_ROUTER_NAMESPACE}/access-keys`,
        permission: ResiPermissions.MANAGE_ACCESS_KEY
    },
    {
        path: `${RESI_ROUTER_NAMESPACE}/occupants`,
        permission: ResiPermissions.VIEW_OCCUPANTS
    },
    {
        path: `${RESI_ROUTER_NAMESPACE}/vehicles`,
        permission: ResiPermissions.VIEW_VEHICLES
    },
    {
        path: `${RESI_ROUTER_NAMESPACE}/resi-rentals`,
        permission: ResiPermissions.MANAGE_RENTALS
    },
    {
        path: `${RESI_ROUTER_NAMESPACE}/support`,
        permission: null
    }
];

export function getFirstAvailableResiRoute(can) {
    const route = RESI_ROUTE_FALLBACKS.find(route => {
        if (!route.permission) return true;

        return can(route.permission);
    });

    return route?.path || `${RESI_ROUTER_NAMESPACE}/support`;
}

const ResiGuard = ({ children, authUser }) => {
    const location = useLocation();
    const { user } = useAuth();
    const { can, accessSummary } = useResiAccess();

    const pathname = location.pathname;

    const isChecking = !user || !accessSummary;

    const globalAccessLevel = accessSummary?.globalAccessLevel;

    const isFullyGated = globalAccessLevel === PropertyAccessLevel.GATED;
    // Any gated/limited property (incl. 'mixed' global level) lets the user open
    // upload-identification voluntarily; only fully gated users are force-redirected.
    const requiresVerification =
        accessSummary?.gated?.length > 0 || accessSummary?.limited?.length > 0;

    const isUploadIdentificationRoute = resiRouteMatchers.uploadIdentification(pathname);

    const routeDecision = useMemo(() => {
        return getRouteAccessDecision({
            pathname,
            can,
            rules: resiRouteAccessRules
        });
    }, [pathname, can]);

    if (!authUser) {
        return <Navigate to="/login" replace />;
    }

    if (isChecking) {
        return null;
    }
    const fallbackPath = getFirstAvailableResiRoute(can);

    if (isFullyGated && !isUploadIdentificationRoute) {
        const uploadRedirectKey = `upload-identification-redirected-${user?.uid}`;
        const hasAlreadyRedirected = sessionStorage.getItem(uploadRedirectKey) === 'true';

        if (!hasAlreadyRedirected) {
            sessionStorage.setItem(uploadRedirectKey, 'true');

            return (
                <Navigate
                    to={`${RESI_ROUTER_NAMESPACE}/upload-identification`}
                    replace
                    state={{ from: location }}
                />
            );
        }
    }
    // TODO: fix this route to navigate to dashboard/home , for now the guests page is our home
    if (!requiresVerification && isUploadIdentificationRoute) {
        return <Navigate to={fallbackPath} replace state={{ from: location }} />;
    }

    if (pathname === RESI_ROUTER_NAMESPACE) {
        return <Navigate to={fallbackPath} replace />;
    }

    if (!routeDecision.allowed) {
        return <Navigate to={fallbackPath} replace />;
    }

    return children;
};

const mapStateToProps = ({ auth }) => ({
    authUser: auth.authUser
});

export default connect(mapStateToProps)(ResiGuard);
